import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Megaphone, HandCoins, Link2, CalendarClock, Tv, ArrowRight, Mail, Send,
} from "lucide-react";
import { Input, Select } from "../components/UI";
import { EmailDomainSetup } from "../components/EmailDomainSetup";
import { BellRing, BarChart3, Newspaper, Activity } from "lucide-react";

const ALERT_ICONS = { generic_shift: BarChart3, poll_news: Activity, news_surge: Newspaper };

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function WarRoom() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    api.get("/war-room/overview").then(r => setData(r.data));
    api.get("/war-room/email-settings").then(r => setEmailSettings(r.data));
    api.get("/war-room/domain-status").then(r => setDomainStatus(r.data)).catch(() => {});
    api.get("/political-intelligence/alerts").then(r => setAlerts(r.data)).catch(() => {});
  }, []);

  const markAlertsRead = async () => {
    try {
      await api.post("/political-intelligence/alerts/read-all");
      const r = await api.get("/political-intelligence/alerts");
      setAlerts(r.data);
    } catch { /* noop */ }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/war-room/digest-test");
      toast.success(`Weekly digest sent to ${r.data.sent} claimed candidate(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest send failed"); }
    finally { setSendingDigest(false); }
  };

  const saveEmail = async (patch) => {
    setSavingEmail(true);
    try {
      const r = await api.put("/war-room/email-settings", { ...emailSettings, ...patch });
      setEmailSettings(r.data);
      toast.success("Email settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingEmail(false); }
  };

  const sendTest = async () => {
    if (!emailSettings?.recipient) { toast.error("Set a recipient email first, then Save"); return; }
    setSendingTest(true);
    try {
      const r = await api.post("/war-room/email-test");
      toast.success(`Brief emailed to ${r.data.recipient}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingTest(false); }
  };

  const runBrief = async () => {
    setBriefLoading(true);
    try {
      const r = await api.post("/war-room/ai-brief");
      setBrief(r.data.brief);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI brief failed"); }
    finally { setBriefLoading(false); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading war room…</div>;

  const { election, advertising, fundraising, attribution } = data;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Live · Unified Command"
        title="Campaign War Room"
        subtitle="Advertising, fundraising, and attribution — one screen, updated live."
        action={
          <Btn onClick={runBrief} disabled={briefLoading} data-testid="ai-brief-btn">
            {briefLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI daily brief
          </Btn>
        }
      />

      <Card className="border-cyan-400/30" data-testid="countdown-card">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-8 h-8 text-cyan-300" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Election day</div>
              <div className="font-display text-xl font-bold">{election.election_date ? String(election.election_date).slice(0, 10) : "Not set"}</div>
            </div>
          </div>
          {election.days_left !== null && (
            <div className="ml-auto text-right">
              <div className="font-display text-5xl font-bold text-cyan-300">{election.days_left}</div>
              <div className="text-xs uppercase tracking-wider text-slate-400">days remaining · {election.entity_name}</div>
            </div>
          )}
        </div>
      </Card>

      {brief && (
        <Card className="border-violet-400/30" data-testid="ai-brief-panel">
          <div className="text-xs uppercase tracking-wider text-violet-300 font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Daily Brief</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{brief}</div>
        </Card>
      )}

      {alerts && (
        <Card data-testid="intel-alerts-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-300 font-semibold">
              <BellRing className="w-4 h-4" /> Intelligence Alerts
              {alerts.unread > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/30 text-rose-300 text-[10px]" data-testid="alerts-unread-count">{alerts.unread} new</span>}
            </div>
            {alerts.alerts.length > 0 && (
              <button onClick={markAlertsRead} data-testid="alerts-mark-read" className="text-xs text-slate-400 hover:text-white">Mark all read</button>
            )}
          </div>
          {alerts.alerts.length === 0 ? (
            <div className="text-sm text-slate-500">No alerts — your race feeds are quiet. Alerts fire automatically when the generic ballot shifts or big race news breaks.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-2">
              {alerts.alerts.slice(0, 6).map(a => {
                const Icon = ALERT_ICONS[a.type] || Activity;
                return (
                  <div key={a.id} className={`flex items-start gap-2.5 rounded-lg border p-2.5 ${a.read ? "border-white/5 opacity-60" : "border-rose-400/20 bg-rose-400/[0.04]"}`} data-testid={`alert-${a.id}`}>
                    <Icon className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm text-white leading-snug">{a.title}</div>
                      <div className="text-xs text-slate-400 truncate">{a.detail}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5">{(a.created_at || "").slice(0, 16).replace("T", " ")} UTC</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid="wr-advertising">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-3"><Megaphone className="w-4 h-4" /> Advertising</div>
          <div className="font-display text-3xl font-bold">{usd(advertising.total_budget)}</div>
          <div className="text-xs text-slate-500 mt-1">total budget · {advertising.total_campaigns} campaigns · {advertising.active_campaigns} active</div>
          <div className="mt-4 space-y-2">
            {advertising.synced_campaigns.map(c => (
              <div key={c.local_campaign_id} className="flex items-center gap-2 text-xs">
                <Tv className="w-3.5 h-3.5 text-cyan-300" />
                <span className="text-slate-300 truncate flex-1">{c.name}</span>
                <span className="font-mono text-slate-500">SA #{c.sa_id}</span>
              </div>
            ))}
            {advertising.synced_to_stackadapt === 0 && <div className="text-xs text-slate-600">No campaigns synced live yet.</div>}
          </div>
          <button onClick={() => navigate("/campaigns")} className="mt-4 text-xs text-cyan-300 flex items-center gap-1 hover:underline">Manage campaigns <ArrowRight className="w-3 h-3" /></button>
        </Card>

        <Card data-testid="wr-fundraising">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-3"><HandCoins className="w-4 h-4" /> Fundraising</div>
          <div className="font-display text-3xl font-bold">{usd(fundraising.total_raised)}</div>
          <div className="text-xs text-slate-500 mt-1">{fundraising.contributions} contributions · {fundraising.donors} donors</div>
          <div className="mt-4 space-y-2.5">
            {fundraising.goals.map(g => {
              const pct = g.goal ? Math.min(100, g.raised / g.goal * 100) : 0;
              return (
                <div key={g.name}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-300 truncate">{g.name}</span><span className="font-mono text-slate-400">{pct.toFixed(0)}%</span></div>
                  <div className="h-1.5 rounded-full bg-white/10"><div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
          <button onClick={() => navigate("/fundraising")} className="mt-4 text-xs text-emerald-300 flex items-center gap-1 hover:underline">Fundraising dashboard <ArrowRight className="w-3 h-3" /></button>
        </Card>

        <Card data-testid="wr-attribution">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-300 font-semibold mb-3"><Link2 className="w-4 h-4" /> Ads ↔ Fundraising</div>
          <div className="font-display text-3xl font-bold">{usd(attribution.ad_attributed_amount)}</div>
          <div className="text-xs text-slate-500 mt-1">raised from ad-attributed traffic ({attribution.ad_attributed_count} gifts)</div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-md bg-white/[0.03] border border-white/10 p-3">
              <div className="font-display text-xl font-bold">{attribution.microsite_visits}</div>
              <div className="text-[11px] text-slate-500">microsite visits</div>
            </div>
            <div className="rounded-md bg-white/[0.03] border border-white/10 p-3">
              <div className="font-display text-xl font-bold">{attribution.live_microsites}</div>
              <div className="text-[11px] text-slate-500">live microsites</div>
            </div>
          </div>
          <button onClick={() => navigate("/fundraising/transactions")} className="mt-4 text-xs text-amber-300 flex items-center gap-1 hover:underline">Transaction ledger <ArrowRight className="w-3 h-3" /></button>
        </Card>
      </div>

      {emailSettings && (
        <Card data-testid="email-brief-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-violet-300 font-semibold mb-1"><Mail className="w-4 h-4" /> Daily brief email</div>
          <p className="text-xs text-slate-500 mb-4">The AI brief is generated and emailed automatically every morning via Mailgun.</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <Input label="Recipient" value={emailSettings.recipient || ""} onChange={e => setEmailSettings({ ...emailSettings, recipient: e.target.value })} placeholder="you@campaign.com" className="md:col-span-2" data-testid="brief-recipient-input" />
            <Select label="Send time (ET)" value={String(emailSettings.hour)} onChange={e => setEmailSettings({ ...emailSettings, hour: +e.target.value })} data-testid="brief-hour-select">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
            </Select>
            <Input label="Sending domain (Mailgun)" value={emailSettings.domain || ""} onChange={e => setEmailSettings({ ...emailSettings, domain: e.target.value })} data-testid="brief-domain-input" />
            <label className="flex items-center gap-2 text-sm text-slate-300 pb-2.5">
              <input type="checkbox" checked={!!emailSettings.enabled} onChange={e => setEmailSettings({ ...emailSettings, enabled: e.target.checked })} data-testid="brief-enabled-toggle" />
              Enabled
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Btn variant="secondary" onClick={() => saveEmail({})} disabled={savingEmail} data-testid="brief-save-btn">
              {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save settings
            </Btn>
            <Btn onClick={sendTest} disabled={sendingTest} data-testid="brief-send-test-btn">
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send now
            </Btn>
            <Btn variant="secondary" onClick={sendDigest} disabled={sendingDigest} data-testid="digest-send-btn">
              {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send weekly digest now
            </Btn>
            {emailSettings.last_sent_at && <span className="text-xs text-slate-500">Last brief: {emailSettings.last_sent_at.slice(0, 16).replace("T", " ")} UTC</span>}
          </div>
          {domainStatus && (
            <div className="mt-3 text-[11px] flex flex-wrap gap-3" data-testid="domain-status">
              <span className="text-slate-500">Sending domain <span className="font-mono text-slate-300">{domainStatus.current.domain}</span>: <span className={domainStatus.current.state === "active" ? "text-emerald-300" : "text-amber-300"}>{domainStatus.current.state}</span></span>
            </div>
          )}
          <EmailDomainSetup domainStatus={domainStatus} onSwitched={() => { api.get("/war-room/domain-status").then(r => setDomainStatus(r.data)).catch(() => {}); api.get("/war-room/email-settings").then(r => setEmailSettings(r.data)).catch(() => {}); }} />
          <div className="mt-2 text-[11px] text-slate-600">
            Automatic emails also active: candidate kit welcome (on claim approval) · donor thank-you receipts (on payment) · weekly race digest (Mondays 8 AM ET to claimed candidates).
          </div>
        </Card>
      )}
    </div>
  );
}
